import React, { useState, useRef, useEffect } from "react";

export interface ParsedFilter {
  key: string;
  value: string;
  op?: ":" | "=" | ">" | "<" | ">=" | "<=";
}

interface GenericSearchbarProps {
  placeholder?: string;
  suggestedKeys?: string[];
  onFiltersChange: (filters: ParsedFilter[]) => void;
  liveFiltering?: boolean;
  debounceMs?: number;
}

const parseInputToFilter = (input: string): ParsedFilter => {
  const match = input.match(/^\s*(.+?)\s*[:=]\s*(.+?)\s*$/);
  if (match) return { key: match[1], value: match[2] };
  return { key: "", value: input.trim() };
};

const GenericSearchbar: React.FC<GenericSearchbarProps> = ({
  placeholder = "Find by attribute or value (case-sensitive)",
  suggestedKeys = [],
  onFiltersChange,
  liveFiltering = true,
  debounceMs = 200,
}) => {
  const [input, setInput] = useState("");
  const [filters, setFilters] = useState<ParsedFilter[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [liveFilter, setLiveFilter] = useState<ParsedFilter | null>(null);
  const [hasChosenKey, setHasChosenKey] = useState(false);

  const leftSide =
    input.split(/[=:]/, 1)[0]?.trim().toLowerCase() ||
    input.trim().toLowerCase();
  const filteredKeys = suggestedKeys.filter((key) =>
    key.toLowerCase().includes(leftSide)
  );

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced live typing -> update ONLY local liveFilter
  useEffect(() => {
    if (!liveFiltering) return;

    if (debounceTimer.current !== null) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }

    debounceTimer.current = setTimeout(() => {
      const lf = parseInputToFilter(input);
      if (hasChosenKey && lf.key && lf.value.trim()) {
        // live contains while typing
        setLiveFilter({ ...lf, op: ":" });
      } else {
        setLiveFilter(null);
      }
    }, debounceMs);

    return () => {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, [input, liveFiltering, debounceMs, hasChosenKey]);

  // The ONLY place we notify the parent (post-render)
  useEffect(() => {
    if (liveFiltering && liveFilter && liveFilter.value.trim()) {
      onFiltersChange([...filters, liveFilter]);
    } else {
      onFiltersChange(filters);
    }
  }, [filters, liveFilter, liveFiltering, onFiltersChange]);

  // Commit on Enter — no parent call here
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && input.trim() !== "") {
      const f = parseInputToFilter(input.trim());
      setFilters((prev) => [...prev, { key: f.key, value: f.value }]);
      setInput("");
      setLiveFilter(null);
      setHasChosenKey(false);
      setShowSuggestions(false);
    } else if (e.key === "Escape") {
      setInput("");
      setLiveFilter(null);
      setHasChosenKey(false);
      setShowSuggestions(false);
      // parent is updated by the effect
    }
  };

  const handleRemoveFilter = (idx: number) => {
    setFilters((prev) => prev.filter((_, i) => i !== idx));
    // parent is updated by the effect
  };

  const handleSuggestionClick = (key: string) => {
    setHasChosenKey(true);
    setInput(key + " = ");
    setShowSuggestions(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (input.trim() === "") setHasChosenKey(false);
  }, [input]);

  return (
    <div className="w-full relative">
      <div className="w-full flex items-center bg-semantic-bg-primary-alt border border-semantic-border-secondary rounded-sm px-3 py-2 shadow-sm">
        <svg
            className="w-5 h-5 text-semantic-text-secondary mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
            />
          </svg>
        <input
          ref={inputRef}
          type="text"
          className="w-full outline-none bg-transparent text-semantic-text-primary placeholder-gray-400"
          placeholder={placeholder}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 100)}
          onKeyDown={handleInputKeyDown}
        />
      </div>

      {showSuggestions && leftSide && filteredKeys.length > 0 && (
        <div className="absolute mt-1 bg-semantic-bg-primary-alt border border-semantic-border-primary rounded-sm shadow-lg z-10 max-h-60 overflow-auto w-full">
          <div className="px-4 py-2 text-xs text-semantic-text-secondary border-b">
            Suggested filters
          </div>
          {filteredKeys.map((key) => (
            <div
              key={key}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-body"
              onMouseDown={() => handleSuggestionClick(key)}
            >
              {key} =
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-2">
        {filters.map((f, idx) => (
          <span
            key={idx}
            className="inline-flex items-center bg-semantic-bg-tertiary text-semantic-text-secondary px-2 py-1 rounded-full text-sm"
          >
            {f.key ? (
              <>
                <span className="font-semibold">{f.key}</span>
                <span className="mx-1">:</span>
                <span>{f.value}</span>
              </>
            ) : (
              <span>{f.value}</span>
            )}
            <button
              className="ml-2 text-semantic-text-secondary hover:text-semantic-text-danger"
              onClick={() => handleRemoveFilter(idx)}
              aria-label="Remove filter"
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};
export default GenericSearchbar;